import classNames from "classnames";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation, useParams } from "react-router-dom";
import Icon from "../../../components/Icon";
import AddText from "../Add";
import Content2 from "../Content2";
import styles from "./TextEditor.module.sass";

const TextEditor = ({ initialMode = "TEXT" }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();

  // Resolve mode from URL (?view=content). Use window.location for initial state - React Router's
  // location can be empty on first paint (full page load / refresh), so we read the actual URL.
  const [viewMode, setViewMode] = useState(() => {
    const search = typeof window !== "undefined" ? window.location.search : "";
    const view = new URLSearchParams(search).get("view");
    return view === "content" ? "CONTENT" : initialMode;
  });

  const [saveHandler, setSaveHandler] = useState(null);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [loading, setLoading] = useState(false);

  // Content2 guarda su handler en un ref; solo se invoca explícitamente desde el botón
  const contentSaveRef = useRef(null);
  const [contentSaveDisabled, setContentSaveDisabled] = useState(true);

  // ── Portal slots: Content2 renders directly into these DOM nodes ──
  const [filtersSlot, setFiltersSlot] = useState(null);
  const [actionsSlot, setActionsSlot] = useState(null);

  // Sync viewMode when URL changes (browser back, client-side nav). Prefer window.location when
  // React Router's location might lag (e.g. after full page load).
  useEffect(() => {
    const search =
      (typeof window !== "undefined" && window.location.search) || location?.search || "";
    const view = new URLSearchParams(search).get("view");
    setViewMode(view === "content" ? "CONTENT" : initialMode);
  }, [location.search, initialMode]);

  const handleToggle = (mode) => {
    if (mode === viewMode) return;
    setViewMode(mode);
    // Update URL so the view is shareable
    const basePath = `/texts/edit/${id}`;
    if (mode === "CONTENT") {
      history.replace({ pathname: basePath, search: "?view=content" });
    } else {
      history.replace({ pathname: basePath, search: "" });
    }
  };

  const handleSave = async () => {
    if (viewMode === "CONTENT") {
      const fn = contentSaveRef.current;
      if (fn && !contentSaveDisabled) {
        setLoading(true);
        try {
          await fn();
        } finally {
          setLoading(false);
        }
      }
    } else if (saveHandler && !saveDisabled) {
      setLoading(true);
      try {
        await saveHandler();
      } finally {
        setLoading(false);
      }
    }
  };

  const isContent = viewMode === "CONTENT";

  const onRegisterSave = useCallback((fnOrObj) => {
    if (fnOrObj === null) {
      setSaveHandler(null);
      setSaveDisabled(true);
    } else if (typeof fnOrObj === "function") {
      // Store callback by reference; do not execute it as a state updater.
      setSaveHandler(() => fnOrObj);
      setSaveDisabled(false);
    } else if (fnOrObj && typeof fnOrObj.save === "function") {
      // Store callback by reference; do not execute it as a state updater.
      setSaveHandler(() => fnOrObj.save);
      setSaveDisabled(!!fnOrObj.disabled);
    }
  }, []);

  const onContentSaveRef = useCallback((fn, disabled) => {
    contentSaveRef.current = fn ?? null;
    setContentSaveDisabled(disabled ?? true);
  }, []);

  return (
    <div className={styles.body}>
      {/* ═══════════════ Main content area ═══════════════ */}
      {viewMode === "TEXT" && (
        <AddText
          isEdit={!!id}
          embedded
          onRegisterSave={onRegisterSave}
        />
      )}

      {viewMode === "CONTENT" && id && (
        <Content2
          embedded
          onRegisterSave={onRegisterSave}
          onContentSaveRef={onContentSaveRef}
          filtersSlot={filtersSlot}
          actionsSlot={actionsSlot}
        />
      )}

      {/* ═══════════════ Unified bottom bar ═══════════════ */}
      <div className={styles.bottomBar}>
        {/* ─── Row 1: Content2 renders its filters here via portal ─── */}
        <div
          ref={setFiltersSlot}
          className={classNames(styles.barRow, styles.filtersRow, {
            [styles.visible]: isContent,
          })}
        />

        {/* ─── Row 2: global actions ─── */}
        <div className={classNames(styles.barRow, styles.actionsRow)}>
          {/* View toggle */}
          <div className={styles.viewToggle}>
            <button
              type="button"
              className={classNames(styles.viewToggleBtn, {
                [styles.active]: viewMode === "TEXT",
              })}
              onClick={() => handleToggle("TEXT")}
            >
              {t("common.text", "Texto")}
            </button>
            <button
              type="button"
              className={classNames(styles.viewToggleBtn, {
                [styles.active]: viewMode === "CONTENT",
              })}
              onClick={() => handleToggle("CONTENT")}
            >
              {t("common.content", "Contenido")}
            </button>
          </div>

          <div className={styles.sep} />

          {/* Content2 renders extra action buttons (Import) here via portal */}
          <div ref={setActionsSlot} style={{ display: "contents" }} />

          {/* Save */}
          <button
            type="button"
            disabled={loading || (isContent ? contentSaveDisabled : saveDisabled)}
            onClick={handleSave}
            className={styles.btnPrimary}
          >
            {loading ? "..." : t("common.save", "Guardar")}
          </button>

          <div className={styles.sep} />

          {/* Last edit */}
          <div className={styles.lastEdit}>
            <Icon name="check-all" size="14" />
            {t("common.lastEdited", "Última edición")}
            <span>{dayjs(new Date()).format("MMM DD, YYYY - HH:mm")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextEditor;
